import {
    mapOptionTypes,
    RenderableMapOption,
} from 'app/center/asset_panel/asset_options/AssetOptionsPanel';
import { CachedEventRegistry } from 'app/shared/events/cached_event_registry';
import { isBool } from 'app/shared/utilities/validation';
import * as _ from 'lodash';
import { ViewStateService } from 'app/shared/view_state.service';
import { ViewCollection } from 'app/shared/collections/view_collection.service';
import { View } from 'app/shared/collections/view';
import { IMSVisualiser } from 'app/views/visualiser';
import { InitialMapOptionsFactory } from 'app/shared/map_options/InitialMapOptionsFactory';
import { ColorLegendService } from 'app/shared/components/color_legend/ColorLegendService';

/**
 * An easier way to listen for changes in a particular map option,
 * especially when you don't want to pass the entire service around.
 */
export interface ObservableMapOption<T> {
    onChange(fn: (value: T) => void): () => void;

    getValue(): T;

    // setValue(value: T) <-- this could be a thing if you really need
}

/**
 * @ngdoc service
 * @name ims.center.service:MapOptionsService
 * @description
 * Service that stores the state of globally accessible map options that are pulled from the currently selected view.
 */

const sortMapOptionFunction = (a, b) => {
    if (!a && !b) {
        return 0;
    }
    if (!a) {
        return -1;
    }
    if (!b) {
        return 1;
    }
    // If neither care about order, don't adjust.
    if (!a.order && !b.order) {
        return 0;
    }
    // If B cares but A doesn't, B moves up.
    if (!a.order) {
        return 1;
    }
    // If A cares but B doesn't, A moves up.
    if (!b.order) {
        return -1;
    }
    if (a.order < b.order) {
        return -1;
    }
    if (a.order > b.order) {
        return 1;
    }
    return 0;
};

export type MapOptionValueOption = {
    // optional, if non nil will use this instead of value
    displayValue?: string;
    // must be unique
    value: string;
    // used for optgroup tag
    displayGroup?: string;
};

export type MapOptionValue = {
    options: MapOptionValueOption[];
    // In the case of a drop down this ends up being MapOptionValueOption.value
    defaultOption?: string;
    // In the case of a drop down this ends up being MapOptionValueOption.value
    selected?: string;
};

export type MapOptionGroup = {
    name: string;
    description?: string;
    order?: number;
};

interface SavedMapOptions {
    [viewName: string]: { [key: string]: string };
}

export type MapOption = {
    label: string; // Name displayed to users
    description?: string; // maybe displayed as a tooltip
    disabled?: boolean;
    groupAs?: MapOptionGroup; // The section it will be displayed under in the UI
    order?: number; // What order in the list
    fullWidth?: boolean; // If it takes up the full width
    type: mapOptionTypes;
    value: MapOptionValue | any; // oh boiii.. it should be a boolean if it's a checkbox type
    displayButton?: boolean;
    displayButtonDescription?: string;
    displayButtonPanelTag?: string;

    /* Overrides value with MapOptionValue keyed by [dependentOptionKey][dependentOptionValue]
     For example,
     dependentValues: {
         'assetDisplayType': {
            'latency': {'options': ['min', 'max', 'mean'], 'defaultOption': 'mean'},
            'state': {'options': ['mode']}
         }
     }
    */
    dependentValues?: {
        [dependentOptionKey: string]: { [dependentOptionValue: string]: MapOptionValue };
    };
    // depends?: any; //Intended to be a Map<string[], string>
    onChange?: (value) => void;
};

// Insulate ourselves from changes to MapOptionValueOption type
export function createMapOptionValueOption(
    value: string,
    displayValue?: string,
    displayGroup?: string,
): MapOptionValueOption {
    return {
        value,
        // Use the display value if available
        displayValue: displayValue ? displayValue : value,
        displayGroup,
    };
}

// Insulate ourselves from changes to MapOptionValueOption type
export function displayStringToOptionValue(str: string): MapOptionValueOption {
    return createMapOptionValueOption(str);
}

// Insulate ourselves from changes to MapOptionValueOption type
export function extractValueFromOptionValue(opt: MapOptionValueOption) {
    return opt ? opt.value : undefined;
}

export class MapOptionsService {
    private _mapOptionsChangedEventRegistry: CachedEventRegistry;

    private _availableMapOptions: { [key: string]: MapOption };

    private _defaultSelectedMapOptions: { [key: string]: string };

    private _selectedMapOptions: { [key: string]: string };

    private _localStorageService: any;

    private _viewStateService: ViewStateService;

    private _viewCollection: ViewCollection;

    private _initialMapOptionsFactory: InitialMapOptionsFactory;

    private _colorLegendService: ColorLegendService;

    // The key used to reference the map options in local storage
    private _panelKey: string;

    constructor(
        viewStateService: ViewStateService,
        viewCollection: ViewCollection,
        localStorageService,
        panelKey: string,
        initialMapOptionsSetterFactory: InitialMapOptionsFactory,
        colorLegendService: ColorLegendService,
    ) {
        this._availableMapOptions = {};
        this._localStorageService = localStorageService;
        this._panelKey = panelKey;
        this._viewStateService = viewStateService;
        this._viewCollection = viewCollection;
        this._initialMapOptionsFactory = initialMapOptionsSetterFactory;
        this._colorLegendService = colorLegendService;

        this._defaultSelectedMapOptions = {};

        // The selected values of the available map options, keyed by map option key
        this._selectedMapOptions = {};
        this._mapOptionsChangedEventRegistry = new CachedEventRegistry();
    }

    /**
     * Sets a dependentValue for a map option key
     * @param {MapOption} mapOption
     * @param {string} dependsOnKey
     * @param {string} dependsOnValue
     * @param {MapOptionValue} value
     * @return {MapOption}
     */
    static setMapOptionDependentValue(
        mapOption: MapOption,
        dependsOnKey: string,
        dependsOnValue: string,
        value: MapOptionValue,
    ) {
        if (!mapOption) {
            return null;
        }
        // Make sure dependentValues exists
        if (!('dependentValues' in mapOption)) {
            mapOption.dependentValues = {};
        }
        // Make sure the dependent option key name exists
        if (!(dependsOnKey in mapOption.dependentValues)) {
            mapOption.dependentValues[dependsOnKey] = {};
        }

        mapOption.dependentValues[dependsOnKey][dependsOnValue] = value;
        return mapOption;
    }

    /**
     * initial entry point when each view is set up.
     * @param availableMapOptions
     * @param defaultMapOptions
     * @param assetImsVisualisers
     * @param view
     */
    public setNewMapOptions(
        availableMapOptions: { [key: string]: MapOption },
        defaultMapOptions: { [key: string]: string },
        view: View,
        assetImsVisualisers: { [key: string]: IMSVisualiser } = null,
    ) {
        const initialMapOptions = this._initialMapOptionsFactory.create(
            view,
            this._panelKey,
            assetImsVisualisers,
        );

        const { defaultSelectedMapOptions, selectedMapOptions } =
            initialMapOptions.getNewDefaultMapOptions(availableMapOptions, defaultMapOptions);

        this._availableMapOptions = availableMapOptions;
        this._defaultSelectedMapOptions = defaultSelectedMapOptions;
        this._selectedMapOptions = selectedMapOptions;

        // Set dependentValues on available map options when view changes
        for (let key of Object.keys(this._availableMapOptions)) {
            if (this._availableMapOptions[key].dependentValues) {
                this._setMapOptionValueForDependentValues(this._availableMapOptions[key]);
            }
        }

        // Merge current map options with available
        this._selectedMapOptions = this._mergeVisualisationOptions();

        // save the view defaults merged with the current options
        this.updateSelectedMapOptions(this._selectedMapOptions);
    }

    /**
     * Merge the available map options for the view with the current map options.
     * If there is a current map option that isn't in
     * the available map options dict, we will use the default options.
     * @return: The resultant dict
     */
    private _mergeVisualisationOptions() {
        let selectedMapOptions = Object.assign({}, this._selectedMapOptions);

        if (!_.isEmpty(selectedMapOptions)) {
            return Object.assign({}, this._defaultSelectedMapOptions);
        } else {
            return Object.keys(this._availableMapOptions).reduce((result, optionKey) => {
                if (this._availableMapOptions[optionKey].type == mapOptionTypes.checkbox) {
                    // oh god, we're a checkbox, handle everything a bit differently
                    const currentValue = selectedMapOptions[optionKey];
                    return Object.assign({}, result, {
                        [optionKey]: isBool(currentValue)
                            ? currentValue
                            : this._defaultSelectedMapOptions[optionKey],
                    });
                }
                const allowedList = this._availableMapOptions[optionKey].value.options.map(
                    extractValueFromOptionValue,
                );
                // Try and find the stored value in the list of available options for that key
                // for that view
                if (optionKey in selectedMapOptions) {
                    let storedValue = selectedMapOptions[optionKey];
                    if (allowedList.includes(storedValue)) {
                        return Object.assign({}, result, {
                            [optionKey]: storedValue,
                        });
                    }
                }
                // we didn't find it, use the default value
                return Object.assign({}, result, {
                    [optionKey]: this._defaultSelectedMapOptions[optionKey],
                });
            }, {});
        }
    }

    get selectedMapOptions() {
        return this._selectedMapOptions;
    }

    get availableMapOptions() {
        return this._availableMapOptions;
    }

    public isValidMapOption(value: string): boolean {
        return value !== null;
    }

    public setSelectedMapOption(key: string, value: string): void {
        let o = {};
        o[key] = value;
        if (this.isValidMapOption(value)) {
            this.updateSelectedMapOptions(o);
        }
    }

    public updateSelectedMapOptions(changedMapOptions: { [key: string]: string }): void {
        _.assign(this._selectedMapOptions, changedMapOptions);

        for (let key in this.availableMapOptions) {
            this._refreshMapOption(key);
        }

        this._mapOptionsChangedEventRegistry.notifyListeners(
            changedMapOptions,
            this.availableMapOptions,
        );

        const mapOptionsToSave = Object.keys(this.availableMapOptions).reduce(
            (result, optionKey) => {
                return Object.assign({}, result, {
                    [optionKey]: this._selectedMapOptions[optionKey],
                });
            },
            {},
        );
        let newLocalStorageObject = this._mergeMapOptionsWithSavedOptionsInLocalStorage();
        newLocalStorageObject[this._viewStateService.getCurrentView().name] = mapOptionsToSave;
        this._localStorageService.set(this._panelKey, newLocalStorageObject);
    }

    private _mergeMapOptionsWithSavedOptionsInLocalStorage(): SavedMapOptions {
        let newLocalStorageObject = {};

        let views: Array<View> = this._viewCollection.getAllAsArray();

        let viewNames = views.map((view: View) => {
            return view.name;
        });

        let existingLocalStorageOptions = this._localStorageService.get(this._panelKey);

        if (existingLocalStorageOptions) {
            Object.keys(existingLocalStorageOptions).map((key: string) => {
                if (viewNames.includes(key)) {
                    newLocalStorageObject[key] = existingLocalStorageOptions[key];
                }
            });
        }
        return newLocalStorageObject;
    }

    /**
     * This returns renderable versions of the MapOptions (RenderableMapOption objects)
     * This is currently just used for react, and passes through the onChange listener to update this
     * service on changes.
     * @return {RenderableMapOption[]}
     */
    getRenderableMapOptions(): RenderableMapOption[] {
        const keys = Object.keys(this.availableMapOptions);
        const groups = {};

        // Retrieves the groups from the options
        const options = keys
            .map((key) => {
                let o = this.availableMapOptions[key];
                // straight up don't include the map option if it's disabled
                if (!o || o.disabled) {
                    return null;
                }
                if (o.groupAs) {
                    if (!groups[o.groupAs.name]) {
                        groups[o.groupAs.name] = {
                            name: o.groupAs.name,
                            description: o.groupAs.description,
                            order: o.groupAs.order,
                            options: [],
                        };
                    }
                    groups[o.groupAs.name].options.push(o);
                    return null;
                }
                return o;
            })
            // Remove out nulls
            .filter((o) => {
                return o;
            });

        // This adds in groups to the options object that is returned
        const groupKeys = Object.keys(groups);
        groupKeys.map((groupName) => {
            options.push(
                this._buildGroup(
                    groups[groupName],
                    groups[groupName].options.sort(sortMapOptionFunction),
                ),
            );
        });

        return options.sort(sortMapOptionFunction);
    }

    /**
     * Updates the values on the map option based on selected and dependentOptions
     * @param {string} key
     * @return {MapOption}
     * @private
     */
    private _refreshMapOption(key: string): MapOption {
        // if no option available, return null
        if (!this.availableMapOptions[key]) {
            return null;
        }
        const mapOption = this.availableMapOptions[key];

        // Handle swapping standard options for dependent values if dependentValues specified
        if (mapOption.dependentValues) {
            this._setMapOptionValueForDependentValues(mapOption);
        }

        if (mapOption.type == mapOptionTypes.checkbox) {
            mapOption.value = this._selectedMapOptions[key];
        } else if (mapOption.type == mapOptionTypes.dropdown) {
            // Set selected to last selected value for this map option if possible
            const values = mapOption.value.options.map(extractValueFromOptionValue);
            if (values.includes(this._selectedMapOptions[key])) {
                mapOption.value.selected = this._selectedMapOptions[key];
            } else if (mapOption.value.defaultOption) {
                // Otherwise if that option isn't available use the default
                mapOption.value.selected = mapOption.value.defaultOption;
            } else if (mapOption.value.options.length > 0) {
                // Otherwise if the default isn't available, use the 0th option
                mapOption.value.selected = extractValueFromOptionValue(mapOption.value.options[0]);
            }
            // Ensure top level selectedMapOptions matches any changes made here
            // this._selectedMapOptions[key] = mapOption.value.selected;
        } else {
            throw new Error('unhandled map option type! exploring unseen IMS lands');
        }

        return mapOption;
    }

    private _setMapOptionValueForDependentValues(option: MapOption) {
        for (let optionName in option.dependentValues) {
            let dependentValuesForOptionName = option.dependentValues[optionName];

            // If the currently selected value for a dependentValues optionName (eg assetDisplayType) is
            // in the dict of values in dependentValues, set this option's value to the relevant value
            let selectedOptionForOptionName = this._selectedMapOptions[optionName];

            if (selectedOptionForOptionName in dependentValuesForOptionName) {
                option.value = dependentValuesForOptionName[selectedOptionForOptionName];
            }
        }
        return option;
    }

    private _buildGroup(
        group: MapOptionGroup,
        mapOptions: RenderableMapOption[],
    ): RenderableMapOption {
        return {
            label: group.name,
            description: group.description,
            order: group.order,
            type: mapOptionTypes.group,
            value: mapOptions,
            fullWidth: true,
            onChange: () => null,
        };
    }

    addOnMapOptionsChanged(
        fn: (changedMapOptions: { [mapOptionKey: string]: any }) => any,
    ): () => void {
        return this._mapOptionsChangedEventRegistry.addEventListener(fn);
    }

    /**
     * Allows you to disabled or enable a map option whenever necessary.
     * @param mapOptionName The map option magic string to mutate
     * @param isDisabled Should this map option be disabled
     */
    setIsMapOptionDisabled(mapOptionName: string, isDisabled: boolean) {
        if (mapOptionName in this.availableMapOptions) {
            const isCurrentlyDisabled = this.availableMapOptions[mapOptionName].disabled;
            // only make the change if necessary
            if (isCurrentlyDisabled !== isDisabled) {
                this.availableMapOptions[mapOptionName].disabled = isDisabled;
                // no map option values actually changed, just the disabled property
                this._mapOptionsChangedEventRegistry.notifyListeners({});
            }
        }
    }

    /**
     * A simpler way to listen for changes in a particular map option.
     * Especially when you don't want to pass the entire service around.
     * Maybe at some point we could add a setValue function on the interface too
     * @param mapOptionKey
     */
    public createObservableForValue(mapOptionKey: string): ObservableMapOption<boolean> {
        return {
            onChange: (fn: (value: boolean) => void): (() => void) => {
                return this.addOnMapOptionsChanged(() => {
                    const value = this.selectedMapOptions[mapOptionKey];
                    return fn(value as any);
                });
            },
            getValue: (): boolean => {
                const value = this.selectedMapOptions[mapOptionKey];
                return value as any;
            },
        };
    }
}
